import { useEffect, useRef } from 'react'

/**
 * How a blocked save finds its way back to the field that blocked it.
 *
 * bindx refuses to send an entity that carries a client-side validation error
 * and reports it by its internal id — `Entity Block:__temp_b78a…`, which tells
 * an editor nothing. Anything that can hide such an entity from the page (a
 * block whose form lives in a closed drawer, a section behind a tab) registers
 * itself here, so the persist button can name it in Czech and put it back on
 * screen instead.
 */
export interface ValidationTarget {
	/** Names the thing in a sentence: „Blok „Úvodní hero“ má nevyplněné…“. */
	label: string
	/** Brings it back in front of the editor — expands it, scrolls to it, marks it. */
	reveal: () => void
}

// Module-level rather than a context: the persist button and the block that
// blocks it sit in different subtrees (the button is in a sticky page header,
// the block deep inside a repeater), and a provider spanning both would have to
// wrap every route.
const targets = new Map<string, ValidationTarget>()

const targetKey = (entityType: string, entityId: string): string => `${entityType}:${entityId}`

/** Registers a target for as long as the component stays mounted. */
export function useValidationTarget(entityType: string, entityId: string, target: ValidationTarget): void {
	// The registration is by entity, not by render: `label` and `reveal` are
	// rebuilt on every render, and re-registering that often would churn the map
	// for nothing. The entry reads through to the latest props instead.
	const latest = useRef(target)
	latest.current = target

	useEffect(() => {
		const key = targetKey(entityType, entityId)
		const entry: ValidationTarget = {
			get label(): string {
				return latest.current.label
			},
			reveal: () => latest.current.reveal(),
		}
		targets.set(key, entry)

		return () => {
			// Only if it is still ours — a remount for the same entity registers
			// its own entry before this cleanup runs.
			if (targets.get(key) === entry) targets.delete(key)
		}
	}, [entityType, entityId])
}

export function findValidationTarget(entityType: string, entityId: string): ValidationTarget | undefined {
	return targets.get(targetKey(entityType, entityId))
}

const invalidFieldSelector = 'input:invalid, textarea:invalid, select:invalid, [aria-invalid="true"]'

/**
 * Fallback for a form that registered no target: the browser already knows
 * which control it refused, so scroll to the first one and put the caret in it.
 *
 * `preventScroll` because `focus()` would jump the page instantly and undo the
 * smooth scroll started a line earlier.
 */
export function revealFirstInvalidField(root: ParentNode = document): boolean {
	const field = root.querySelector<HTMLElement>(invalidFieldSelector)
	if (!field) return false

	field.scrollIntoView({ behavior: 'smooth', block: 'center' })
	field.focus({ preventScroll: true })
	return true
}
